import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type InputHTMLAttributes,
  type RefObject,
} from 'react'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CloseIcon,
  ExternalLinkIcon,
  InstallIcon,
  PlayIcon,
  ShieldCheckIcon,
} from './Icons'

export type PanelMode =
  'closed' | 'available' | 'installed' | 'story' | 'inquiry' | 'confirmation'

export interface PanelSponsor {
  name: string
  paidAmount: number
  description: string
  installedMonth: string
  proofVerified: boolean
  website?: string
  posterUrl?: string
  posterAlt?: string
}

export interface PanelZone {
  id: string
  number: number | string
  status: 'available' | 'reserved' | 'installed'
  roadPixels: number
  price: number
  location: string
  sponsor?: PanelSponsor
}

export interface PanelStoryEpisode {
  id: string
  number: number
  title: string
  body: string
  date?: string
  posterUrl?: string
  posterAlt?: string
}

export interface DetailPanelProps {
  mode: PanelMode
  zone: PanelZone | null
  episodes?: readonly PanelStoryEpisode[]
  storyIndex?: number
  modal?: boolean
  onRequestClose: () => void
  onRequestBack: () => void
  onRequestInquiry: (zone: PanelZone) => void
  onInquiryReady: () => void
  onStoryIndexChange?: (index: number) => void
  onWatchSponsorStory?: (zone: PanelZone) => void
  onWatchEpisode?: (episode: PanelStoryEpisode) => void
}

interface InquiryValues {
  name: string
  email: string
  brand: string
  website: string
  message: string
}

type RequiredInquiryField = 'name' | 'email' | 'brand' | 'message'
type InquiryErrors = Partial<Record<RequiredInquiryField, string>>

const emptyInquiry: InquiryValues = {
  name: '',
  email: '',
  brand: '',
  website: '',
  message: '',
}

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function formatZoneNumber(value: number | string) {
  return String(value).padStart(2, '0')
}

function validatedHttpsDestination(value?: string) {
  if (!value) return null

  try {
    const destination = new URL(value)
    if (
      destination.protocol !== 'https:' ||
      destination.username ||
      destination.password
    ) {
      return null
    }
    return destination.href
  } catch {
    return null
  }
}

function validatedLocalMediaSource(value?: string) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null
  return value
}

function validateInquiry(values: InquiryValues): InquiryErrors {
  const errors: InquiryErrors = {}

  if (!values.name.trim()) errors.name = 'Enter your name.'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Enter a valid email.'
  }
  if (!values.brand.trim()) errors.brand = 'Enter a brand or company.'
  if (!values.message.trim()) {
    errors.message = 'Describe the proposed artwork or message.'
  }

  return errors
}

export function DetailPanel({
  mode,
  zone,
  episodes = [],
  storyIndex = 0,
  modal = false,
  onRequestClose,
  onRequestBack,
  onRequestInquiry,
  onInquiryReady,
  onStoryIndexChange,
  onWatchSponsorStory,
  onWatchEpisode,
}: DetailPanelProps) {
  const panelRef = useRef<HTMLElement>(null)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const previousModeRef = useRef(mode)
  const inquiryId = useId()
  const [inquiry, setInquiry] = useState<InquiryValues>(emptyInquiry)
  const [errors, setErrors] = useState<InquiryErrors>({})

  const zoneNumber = zone ? formatZoneNumber(zone.number) : ''
  const safeStoryIndex = Math.min(
    Math.max(storyIndex, 0),
    Math.max(episodes.length - 1, 0),
  )
  const episode = episodes[safeStoryIndex]
  const headingId = `${inquiryId}-heading`
  const descriptionId = `${inquiryId}-description`

  function clearInquiry() {
    setInquiry(emptyInquiry)
    setErrors({})
  }

  function handleClose() {
    if (mode === 'confirmation') clearInquiry()
    onRequestClose()
  }

  useEffect(() => {
    const previousMode = previousModeRef.current

    if (
      mode === 'closed' ||
      (previousMode === 'confirmation' && mode !== 'confirmation')
    ) {
      setInquiry(emptyInquiry)
      setErrors({})
    }

    previousModeRef.current = mode
  }, [mode])

  useEffect(() => {
    if (mode !== 'closed') headingRef.current?.focus({ preventScroll: true })
  }, [mode, zone?.id, safeStoryIndex])

  useEffect(() => {
    if (mode === 'closed') return undefined

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        if (mode === 'confirmation') {
          setInquiry(emptyInquiry)
          setErrors({})
        }
        onRequestBack()
        return
      }

      if (!modal || event.key !== 'Tab' || !panelRef.current) return

      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.hidden)

      if (focusable.length === 0) {
        event.preventDefault()
        headingRef.current?.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mode, modal, onRequestBack])

  if (mode === 'closed') return null

  const accent = mode === 'installed' ? 'installed' : 'available'

  return (
    <section
      ref={panelRef}
      className="detail-panel"
      data-mode={mode}
      data-accent={accent}
      role="dialog"
      aria-modal={modal || undefined}
      aria-labelledby={headingId}
      aria-describedby={mode === 'confirmation' ? descriptionId : undefined}
    >
      <button
        className="detail-panel__close"
        type="button"
        onClick={handleClose}
        aria-label="Close panel"
      >
        <CloseIcon className="detail-panel__icon" />
      </button>

      {mode === 'available' && zone ? (
        <AvailableDetail
          headingId={headingId}
          headingRef={headingRef}
          zone={zone}
          zoneNumber={zoneNumber}
          onRequestInquiry={onRequestInquiry}
        />
      ) : null}

      {mode === 'installed' && zone?.sponsor ? (
        <InstalledDetail
          headingId={headingId}
          headingRef={headingRef}
          zone={zone}
          zoneNumber={zoneNumber}
          onWatchSponsorStory={onWatchSponsorStory}
        />
      ) : null}

      {mode === 'story' && episode ? (
        <StoryDetail
          headingId={headingId}
          headingRef={headingRef}
          episode={episode}
          episodeIndex={safeStoryIndex}
          episodeTotal={episodes.length}
          onStoryIndexChange={onStoryIndexChange}
          onWatchEpisode={onWatchEpisode}
        />
      ) : null}

      {mode === 'inquiry' && zone ? (
        <InquiryDetail
          headingId={headingId}
          headingRef={headingRef}
          inquiryId={inquiryId}
          zone={zone}
          zoneNumber={zoneNumber}
          values={inquiry}
          errors={errors}
          onChange={(field, event) => {
            setInquiry((current) => ({
              ...current,
              [field]: event.target.value,
            }))
            if (field !== 'website' && errors[field]) {
              setErrors((current) => ({ ...current, [field]: undefined }))
            }
          }}
          onSubmit={(event) => {
            event.preventDefault()
            const nextErrors = validateInquiry(inquiry)
            setErrors(nextErrors)

            const firstInvalidField = (
              ['name', 'email', 'brand', 'message'] as const
            ).find((field) => nextErrors[field])

            if (firstInvalidField) {
              document
                .getElementById(`${inquiryId}-${firstInvalidField}`)
                ?.focus()
              return
            }

            onInquiryReady()
          }}
        />
      ) : null}

      {mode === 'confirmation' ? (
        <ConfirmationDetail
          headingId={headingId}
          headingRef={headingRef}
          descriptionId={descriptionId}
          onBackToCar={() => {
            clearInquiry()
            onRequestClose()
          }}
        />
      ) : null}
    </section>
  )
}

interface HeadingRefProps {
  headingId: string
  headingRef: RefObject<HTMLHeadingElement | null>
}

function AvailableDetail({
  headingId,
  headingRef,
  zone,
  zoneNumber,
  onRequestInquiry,
}: HeadingRefProps & {
  zone: PanelZone
  zoneNumber: string
  onRequestInquiry: (zone: PanelZone) => void
}) {
  return (
    <div className="detail-panel__content">
      <h2
        ref={headingRef}
        id={headingId}
        className="detail-panel__heading"
        tabIndex={-1}
      >
        Zone {zoneNumber} · available
      </h2>
      <p className="detail-panel__metric">{zone.roadPixels} road pixels</p>
      <p className="detail-panel__value">{currency.format(zone.price)}</p>
      <p className="detail-panel__location">{zone.location}</p>
      <p className="detail-panel__description">
        A prototype placement. Final availability depends on the delivered
        vehicle and installer approval.
      </p>
      <button
        className="detail-panel__primary-action"
        type="button"
        onClick={() => onRequestInquiry(zone)}
      >
        Claim a spot
        <ArrowRightIcon className="detail-panel__action-icon" />
      </button>
    </div>
  )
}

function InstalledDetail({
  headingId,
  headingRef,
  zone,
  zoneNumber,
  onWatchSponsorStory,
}: HeadingRefProps & {
  zone: PanelZone
  zoneNumber: string
  onWatchSponsorStory?: (zone: PanelZone) => void
}) {
  const { sponsor } = zone
  if (!sponsor) return null

  const destination = validatedHttpsDestination(sponsor.website)
  const posterSource = validatedLocalMediaSource(sponsor.posterUrl)

  return (
    <div className="detail-panel__content">
      <p className="detail-panel__state">Zone {zoneNumber} · installed</p>
      <h2
        ref={headingRef}
        id={headingId}
        className="detail-panel__heading"
        tabIndex={-1}
      >
        {sponsor.name}
      </h2>
      <p className="detail-panel__value">
        Paid {currency.format(sponsor.paidAmount)}
      </p>
      <p className="detail-panel__description">{sponsor.description}</p>

      {posterSource && onWatchSponsorStory ? (
        <button
          className="detail-panel__media"
          type="button"
          onClick={() => onWatchSponsorStory(zone)}
          aria-label={`Watch the story for ${sponsor.name}`}
        >
          <img
            className="detail-panel__media-image"
            src={posterSource}
            alt={sponsor.posterAlt ?? ''}
          />
          <span className="detail-panel__media-play" aria-hidden="true">
            <PlayIcon className="detail-panel__media-play-icon" />
          </span>
        </button>
      ) : null}

      <div className="detail-panel__metadata">
        <span className="detail-panel__meta-item">
          <InstallIcon className="detail-panel__meta-icon" />
          Installed · {sponsor.installedMonth}
        </span>
        {sponsor.proofVerified ? (
          <span className="detail-panel__meta-item">
            <ShieldCheckIcon className="detail-panel__meta-icon" />
            Proof verified
          </span>
        ) : null}
      </div>

      {onWatchSponsorStory ? (
        <button
          className="detail-panel__primary-action"
          type="button"
          onClick={() => onWatchSponsorStory(zone)}
        >
          Watch the story
          <ArrowRightIcon className="detail-panel__action-icon" />
        </button>
      ) : null}

      {destination ? (
        <a
          className="detail-panel__text-action"
          href={destination}
          target="_blank"
          rel="noopener noreferrer sponsored"
          aria-label={`Visit ${sponsor.name} (opens in a new tab)`}
        >
          Visit sponsor
          <ExternalLinkIcon className="detail-panel__external-icon" />
        </a>
      ) : null}
    </div>
  )
}

function StoryDetail({
  headingId,
  headingRef,
  episode,
  episodeIndex,
  episodeTotal,
  onStoryIndexChange,
  onWatchEpisode,
}: HeadingRefProps & {
  episode: PanelStoryEpisode
  episodeIndex: number
  episodeTotal: number
  onStoryIndexChange?: (index: number) => void
  onWatchEpisode?: (episode: PanelStoryEpisode) => void
}) {
  const hasPrevious = episodeIndex > 0 && Boolean(onStoryIndexChange)
  const hasNext = episodeIndex < episodeTotal - 1 && Boolean(onStoryIndexChange)
  const posterSource = validatedLocalMediaSource(episode.posterUrl)

  return (
    <div className="detail-panel__content">
      <h2
        ref={headingRef}
        id={headingId}
        className="detail-panel__heading"
        tabIndex={-1}
      >
        Start the story
      </h2>
      <p className="detail-panel__state">
        Episode {episode.number} of {episodeTotal}
      </p>

      {posterSource ? (
        <img
          className="detail-panel__story-poster"
          src={posterSource}
          alt={episode.posterAlt ?? ''}
        />
      ) : null}

      <h3 className="detail-panel__story-title">{episode.title}</h3>
      <p className="detail-panel__description">{episode.body}</p>
      {episode.date ? (
        <p className="detail-panel__story-date">{episode.date}</p>
      ) : null}

      <div className="detail-panel__carousel-actions">
        {hasPrevious ? (
          <button
            className="detail-panel__carousel-action"
            type="button"
            onClick={() => onStoryIndexChange?.(episodeIndex - 1)}
          >
            <ArrowLeftIcon className="detail-panel__action-icon" />
            Previous
          </button>
        ) : null}
        {hasNext ? (
          <button
            className="detail-panel__carousel-action"
            type="button"
            onClick={() => onStoryIndexChange?.(episodeIndex + 1)}
          >
            Next
            <ArrowRightIcon className="detail-panel__action-icon" />
          </button>
        ) : null}
      </div>

      {onWatchEpisode ? (
        <button
          className="detail-panel__primary-action"
          type="button"
          onClick={() => onWatchEpisode(episode)}
        >
          Watch
          <PlayIcon className="detail-panel__action-icon" />
        </button>
      ) : null}
    </div>
  )
}

function InquiryDetail({
  headingId,
  headingRef,
  inquiryId,
  zone,
  zoneNumber,
  values,
  errors,
  onChange,
  onSubmit,
}: HeadingRefProps & {
  inquiryId: string
  zone: PanelZone
  zoneNumber: string
  values: InquiryValues
  errors: InquiryErrors
  onChange: (
    field: keyof InquiryValues,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}) {
  return (
    <div className="detail-panel__content">
      <h2
        ref={headingRef}
        id={headingId}
        className="detail-panel__heading"
        tabIndex={-1}
      >
        Claim a spot
      </h2>
      <p className="detail-panel__context">
        Zone {zoneNumber} · {currency.format(zone.price)}
      </p>

      <form className="detail-panel__form" noValidate onSubmit={onSubmit}>
        <InquiryField
          id={`${inquiryId}-name`}
          label="Name"
          value={values.name}
          error={errors.name}
          autoComplete="name"
          onChange={(event) => onChange('name', event)}
        />
        <InquiryField
          id={`${inquiryId}-email`}
          label="Email"
          value={values.email}
          error={errors.email}
          type="email"
          inputMode="email"
          autoComplete="email"
          onChange={(event) => onChange('email', event)}
        />
        <InquiryField
          id={`${inquiryId}-brand`}
          label="Brand or company"
          value={values.brand}
          error={errors.brand}
          autoComplete="organization"
          onChange={(event) => onChange('brand', event)}
        />
        <InquiryField
          id={`${inquiryId}-website`}
          label="Website (optional)"
          value={values.website}
          type="url"
          inputMode="url"
          autoComplete="url"
          onChange={(event) => onChange('website', event)}
        />

        <div className="detail-panel__field">
          <label
            className="detail-panel__label"
            htmlFor={`${inquiryId}-message`}
          >
            Proposed artwork or message
          </label>
          <textarea
            className="detail-panel__textarea"
            id={`${inquiryId}-message`}
            value={values.message}
            rows={4}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={
              errors.message ? `${inquiryId}-message-error` : undefined
            }
            onChange={(event) => onChange('message', event)}
          />
          {errors.message ? (
            <p
              className="detail-panel__error"
              id={`${inquiryId}-message-error`}
              role="alert"
            >
              {errors.message}
            </p>
          ) : null}
        </div>

        <button className="detail-panel__primary-action" type="submit">
          Submit inquiry
          <ArrowRightIcon className="detail-panel__action-icon" />
        </button>
      </form>
    </div>
  )
}

function InquiryField({
  id,
  label,
  error,
  ...inputProps
}: {
  id: string
  label: string
  error?: string
} & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="detail-panel__field">
      <label className="detail-panel__label" htmlFor={id}>
        {label}
      </label>
      <input
        {...inputProps}
        className="detail-panel__input"
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error ? (
        <p className="detail-panel__error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function ConfirmationDetail({
  headingId,
  headingRef,
  descriptionId,
  onBackToCar,
}: HeadingRefProps & {
  descriptionId: string
  onBackToCar: () => void
}) {
  return (
    <div className="detail-panel__content">
      <h2
        ref={headingRef}
        id={headingId}
        className="detail-panel__heading"
        tabIndex={-1}
      >
        Inquiry ready
      </h2>
      <p className="detail-panel__description" id={descriptionId}>
        This prototype did not send or save your information. No payment was
        taken.
      </p>
      <button
        className="detail-panel__primary-action"
        type="button"
        onClick={onBackToCar}
      >
        Back to the car
        <ArrowRightIcon className="detail-panel__action-icon" />
      </button>
    </div>
  )
}
